"use client"

import { BarChart, TooltipProps as BarChartTooltipProps } from "@/components/tremor/bar-chart";
import { TooltipProps as LineChartTooltipProps } from "@/components/tremor/line-chart";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/convex/_generated/api";
import { AvailableChartColorsKeys, chartColors, getColorClassName } from "@/lib/chartUtils";
import { useRichQuery } from "@/lib/convex";
import { cn } from "@/lib/utils";
import { format } from "numerable";
import React from "react";
import { DateRange, daysInRange, isValidRange } from "./TimeRangeSelector";
import { Skeleton } from "./ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Doc } from "@/convex/_generated/dataModel";

export interface Period {
  period_start: number;
  period_end: number;
}

const kwhEntryMap = {
  export: "Injectie",
  importGrid: "Afname net",
  importPV: "Afname PV",
  importBattery: "Afname batterijen",
} as const

type ValueOf<T> = T[keyof T];

export type KWHEntry = Period & {
  [V in ValueOf<typeof kwhEntryMap>]: number | null;
}

export interface KWHCounterConfig {
  name: string;
  path: string;
  noInjection?: boolean;
  chart: {
    categories: Array<keyof KWHEntry>;
    colors: Array<AvailableChartColorsKeys>;
  };
}

export interface KWHCounterGraphProps {
  name: string
  path: string
  range: DateRange | undefined
  config: KWHCounterConfig["chart"]
  noInjection?: boolean
}

export const KWHCounterGraph = ({ name, path, range, config, noInjection }: KWHCounterGraphProps) => {
  const [data, setData] = React.useState<KWHEntry[]>([])
  const hours = React.useMemo(() => {
    if (!isValidRange(range))
      return 24;
    const diff = range.to.getTime() - range.from.getTime();
    return Math.ceil(diff / (1000 * 60 * 60));
  }, [range]);
  const { data: queryData, isPending } = useRichQuery(api.kwh.listForInterval, isValidRange(range) ? {
    name: path,
    start: range.from.getTime(),
    end: range.to.getTime(),
    interval: hours > 168 ? 1440 : hours > 24 ? 60 : 15,
    // interval: 15
  } : "skip")

  React.useEffect(() => {
    setData([])
    if (queryData) {
      const formattedData: KWHEntry[] = queryData.map((item) => {
        const e = {
          period_start: item.period_start,
          period_end: item.period_end,
          "Injectie": item.export === null ? null : item.export / 1000,
          "Afname net": item.importGrid === null ? null : item.importGrid / 1000,
          "Afname PV": item.importPV === null ? null : item.importPV / 1000,
          "Afname batterijen": item.importBattery === null ? null : item.importBattery / 1000
        }

        if (noInjection) {
          if (e["Afname PV"] !== null && e["Injectie"] !== null && e["Afname PV"] > e["Injectie"]) {
            e["Afname PV"] = (e["Afname PV"] ?? 0) - (e["Injectie"] ?? 0);
          }
        }

        // if ((e["Afname PV"] ?? 0) > 0) {
        //   e["Afname batterijen"] = Math.random() * e["Afname PV"]! // Simulate battery usage for demo purposes
        // } else if ((e["Afname net"] ?? 0) > 0) {
        //   e["Afname batterijen"] = Math.random() * e["Afname net"]! // Simulate battery usage for demo purposes
        // }

        return e;
      });
      setData(formattedData);
    }
  }, [queryData, noInjection]);

  const chartConfig = React.useMemo(() => {
    if (noInjection) {
      return {
        categories: config.categories.filter(c => c !== "Injectie"),
        colors: config.colors.filter((_, index) => config.categories[index] !== "Injectie")
      }
    } else {
      return config
    }
  }, [noInjection, config]);

  const days = daysInRange(range);
  const opts = React.useMemo(() => {
    const opts: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "numeric",
    }
    if (days > 1) {
      opts.weekday = "long";
      opts.day = "numeric";
      opts.month = "short";
      if (days > 7) {
        delete opts.weekday;
        delete opts.hour;
        delete opts.minute;
      }
    }
    return opts;
  }, [days])

  let Chart = <Skeleton className="h-40 w-full" />
  if (!isPending) {
    Chart = <BarChart
      type="stacked"
      className="h-40"
      data={data}
      index="period_start"
      categories={chartConfig.categories}
      colors={chartConfig.colors}
      onValueChange={(value) => { console.log("Value changed:", value) }}
      valueFormatter={(value: number | null) => format(value, "0,000.0 kWh")}
      keyFormatter={(value: number) => new Intl.DateTimeFormat("nl-BE", opts).format(value)}
      intervalType="equidistantPreserveStart"
      tickCount={days > 1 ? days : undefined}
      yAxisWidth={150}
      customTooltip={createPeriodTooltip({ valueFormatter: (value: number | null) => format(value, "0,000.0 kWh") })}
    />
  }

  return (
    <div className="print:break-after-page">
      <h1 className="text-lg font-semibold">{name}</h1>

      {Chart}

      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground justify-center mt-2">
        {
          chartConfig.categories.map((category, index) => (
            <React.Fragment key={category}>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="flex items-center">
                    <span className={cn("size-2 rounded-full mr-1", chartColors[chartConfig.colors[index]].bg)} />
                    {format(data.reduce((acc, curr) => acc + (curr[category] ?? 0), 0), "0,000.0 kWh")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {category} <br />
                  {isValidRange(range) && <KWHPeriod path={path} category={category} range={range} />}
                </TooltipContent>
              </Tooltip>
            </React.Fragment>
          ))
        }
      </div>

      <div className="print:block hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                Periode
              </TableHead>
              {chartConfig.categories.map((category, index) => (
                <TableHead key={index}>
                  {category}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((entry, index) => (
              <TableRow key={index}>
                <TableCell>
                  {
                    (entry.period_end - entry.period_start >= 1000 * 60 * 60 * 24)
                      ? `${new Intl.DateTimeFormat("nl-BE", {
                        weekday: "long",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      }).format(entry.period_start)}`
                      : `${new Intl.DateTimeFormat("nl-BE", {
                        weekday: "long",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      }).format(entry.period_start)} - ${new Intl.DateTimeFormat("nl-BE", {
                        hour: "numeric",
                        minute: "numeric",
                      }).format(entry.period_end)}`
                  }
                </TableCell>
                {chartConfig.categories.map((category, catIndex) => (
                  <TableCell key={catIndex}>
                    {format(entry[category], "0,000.0 kWh")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div >
  )
}

export interface PeriodTooltipCreate {
  valueFormatter: (value: number | null) => string;
}

export const createPeriodTooltip = ({ valueFormatter }: PeriodTooltipCreate) => {

  const startFormatter = (value: number) => new Intl.DateTimeFormat("nl-BE", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  }).format(value);

  const endFormatter = (value: number) => new Intl.DateTimeFormat("nl-BE", {
    hour: "numeric",
    minute: "numeric",
  }).format(value);

  return function Tooltip({ active, payload }: BarChartTooltipProps | LineChartTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;

    const period_start = payload[0].payload.period_start;
    const period_end = payload[0].payload.period_end;
    const isPeriod = typeof period_start === "number" && typeof period_end === "number";

    return (<div
      className={cn(
        "rounded-md border text-sm shadow-md",
        "border-gray-200 dark:border-gray-800",
        "bg-white dark:bg-gray-950",
      )}
    >
      <div className={cn("border-b border-inherit px-4 py-2")}>
        <p className={cn("font-medium text-gray-900 dark:text-gray-50 first-letter:uppercase")}>
          {isPeriod ? (period_end - period_start >= 1000 * 60 * 60 * 24)
            ? `${new Intl.DateTimeFormat("nl-BE", {
              weekday: "long",
              day: "numeric",
              month: "short",
              year: "numeric",
            }).format(period_start)}`
            : `${startFormatter(period_start)} - ${endFormatter(period_end)}`
            : (typeof payload[0].payload.ts === "number") ?
              `${new Intl.DateTimeFormat("nl-BE", {
                weekday: "long",
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
              }).format(payload[0].payload.ts)}`
              : "Onbekende periode"}
        </p>
      </div>
      <div className={cn("space-y-1 px-4 py-2 border-b")}>
        {payload.map(({ value, category, color }, index) => (
          <div key={`id-${index}`} className="flex items-center justify-between space-x-8">
            <div className="flex items-center space-x-2">
              <span
                aria-hidden="true"
                className={cn("size-2 shrink-0 rounded-sm", getColorClassName(color, "bg"))}
              />
              <p
                className={cn(
                  "whitespace-nowrap text-right",
                  "text-gray-700 dark:text-gray-300",
                )}
              >
                {category.replaceAll("_", " ").replace(/^\w/, (c) => c.toUpperCase())}
              </p>
            </div>
            <p
              className={cn(
                "whitespace-nowrap text-right font-medium tabular-nums",
                "text-gray-900 dark:text-gray-50",
              )}
            >
              {valueFormatter(value)}
            </p>
          </div>
        ))}
      </div>
      {
        payload.length > 1 && (
          <div className={cn("space-y-1 px-4 py-2")}>
            <div className="flex items-center justify-between space-x-8">
              <div className="flex items-center space-x-2">
                <p className="font-medium whitespace-nowrap text-right">
                  {isPeriod ? "Totaal periode" : "Totaal"}
                </p>
              </div>

              <p
                className={cn(
                  "whitespace-nowrap text-right font-medium tabular-nums",
                  "text-gray-900 dark:text-gray-50",
                )}
              >
                {valueFormatter(payload.reduce((acc, curr) => acc + (curr.value ?? 0), 0))}
              </p>
            </div>
          </div>
        )
      }
    </div>)
  }
}

interface KWHPeriodInterface {
  path: string;
  category: keyof KWHEntry;
  range: DateRange;
}

const KWHPeriod = ({ path, category, range }: KWHPeriodInterface) => {
  const { data, isSuccess } = useRichQuery(api.kwh.firstAndLastForPathInRange, isValidRange(range) ? {
    name: path,
    start: range.from.getTime(),
    end: range.to.getTime(),
  } : "skip")

  if (!isSuccess) {
    return null
  }

  const key = Object.entries(kwhEntryMap).find(([_, v]) => v === category)?.[0]
  const first = data.first[key as keyof Doc<"udt_kWh_Counter">] as number;
  const last = data.last[key as keyof Doc<"udt_kWh_Counter">] as number;
  const value = (last - first) / 1000;

  return (<>
    <div>
      <div>
        First: {format(first / 1000, "0,000.0 kWh")}
        {" @ "}
        {new Intl.DateTimeFormat("nl-BE", {
          dateStyle: "long",
          timeStyle: "medium"
        }).format(data.first.ts)}
      </div>
      <div>
        Last: {format(last / 1000, "0,000.0 kWh")}
        {" @ "}
        {new Intl.DateTimeFormat("nl-BE", {
          dateStyle: "long",
          timeStyle: "medium"
        }).format(data.last.ts)}

      </div>
      <div>
        Total:
        {format(value, "0,000.0 kWh")}
      </div>
    </div>
  </>)
}